import { Container, LinearProgress, Typography } from '@mui/material'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import { ReactElement, useEffect, useState } from 'react'
import ViewEntity from '../../ViewEntity'
import {
    Applications,
    IN_TRANSIT_TABS,
    publicShipmentView,
    URLSearchParamsKeys,
} from '../../../constants/constants'
import { useTranslation } from 'react-i18next'
import {
    getApplicationSettingsForGuest,
    getShipmentByShipmentId,
} from '../../../services/guestServices'
import { IApplicationSettingsType } from '../interfaces'
import { useAppDispatch, useAppSelector } from '../../../redux/hook'
import { updateSettings } from '../../../redux/slices/inTransitSlice'
import { useParams, useLocation } from 'react-router-dom'
import { setMultipleSearchParams } from '../../../redux/slices/searchParamsSlice'
import { updateLoader } from '../../../redux/slices/loaderSlice'

const ShipmentPreviewGuest = (): ReactElement => {
    const [isLoadingSettings, setIsLoadingSettings] = useState(true)
    const publicSearchOrgId = useAppSelector(
        (state) => state.searchParams.orgId,
    )
    const dispatch = useAppDispatch()

    useEffect(() => {
        setIsLoadingSettings(true)
        const getAppSettings = async () => {
            const appSettingsResponse =
                await getApplicationSettingsForGuest<IApplicationSettingsType>(
                    publicSearchOrgId || '',
                    Applications.inTransit.id,
                )
            setIsLoadingSettings(false)
            const settings = appSettingsResponse?.appSettings
            dispatch(updateSettings(settings))
        }
        getAppSettings()
    }, [])
    if (isLoadingSettings) {
        return <LinearProgress />
    }

    return <ViewEntity />
}

export default ShipmentPreviewGuest

export const ShipmentPreviewGuest2 = (): ReactElement => {
    const [isLoadingShipment, setIsLoadingShipment] = useState(true)
    const { shipmentId } = useParams()
    const location = useLocation()
    const [errorMessage, setErrorMessage] = useState<string | null>(null)
    const dispatch = useAppDispatch()
    const { t } = useTranslation()
    useEffect(() => {
        // If shipment details are passed via location.state, use them directly
        const shipmentFromState = location.state?.shipment
        if (shipmentFromState && !shipmentFromState?.publicSearchEnabled) {
            setErrorMessage(t('publicSearchDisabled'))
        } else if (shipmentFromState && shipmentFromState.shipmentId) {
            dispatch(
                setMultipleSearchParams({
                    [URLSearchParamsKeys.templateId]:
                        shipmentFromState.shipmentTemplateId,
                    [URLSearchParamsKeys.instanceId]:
                        shipmentFromState.shipmentId,
                    [URLSearchParamsKeys.orgId]: shipmentFromState.orgId,
                    [URLSearchParamsKeys.isGuestUser]: 'true',
                    [URLSearchParamsKeys.applicationId]:
                        Applications.inTransit.id,
                    [URLSearchParamsKeys.tabIndex]:
                        IN_TRANSIT_TABS.MAP_VIEW.toString(),
                    [URLSearchParamsKeys.publicShipmentView]:
                        publicShipmentView.SHIPMENT_PREVIEW,
                }),
            )
            setIsLoadingShipment(false)
            dispatch(updateLoader(false))
            return
        }
        setIsLoadingShipment(true)
        const getShipmentDetails = async () => {
            try {
                const shipment = await getShipmentByShipmentId(shipmentId || '')
                if (!shipment.publicSearchEnabled) {
                    setErrorMessage(t('publicSearchDisabled'))
                    return
                }
                if (shipment && shipment.shipmentId) {
                    dispatch(
                        setMultipleSearchParams({
                            [URLSearchParamsKeys.templateId]:
                                shipment.shipmentTemplateId,
                            [URLSearchParamsKeys.instanceId]:
                                shipment.shipmentId,
                            [URLSearchParamsKeys.orgId]: shipment.orgId,
                            [URLSearchParamsKeys.isGuestUser]: 'true',
                            [URLSearchParamsKeys.applicationId]:
                                Applications.inTransit.id,
                            [URLSearchParamsKeys.tabIndex]:
                                IN_TRANSIT_TABS.MAP_VIEW.toString(),
                            [URLSearchParamsKeys.publicShipmentView]:
                                publicShipmentView.SHIPMENT_PREVIEW,
                        }),
                    )
                }
            } catch (error: unknown) {
                const errorMessage =
                    error instanceof Error
                        ? decodeURIComponent(error?.message)
                        : t('An error occurred')
                setErrorMessage(errorMessage)
            } finally {
                setIsLoadingShipment(false)
            }
        }
        getShipmentDetails()
        /**
         * By default the app starts with loader as true. This is set to false
         * after the user is logged in and an org is set. For guest user these
         * steps are not followed. Hence we are setting false here.
         */
        dispatch(updateLoader(false))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])
    if (isLoadingShipment) {
        return <LinearProgress />
    }
    if (errorMessage) {
        return (
            <Container
                maxWidth="sm"
                sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    textAlign: 'center',
                    gap: 2,
                }}
            >
                <ErrorOutlineIcon color="error" sx={{ fontSize: '4rem' }} />
                <Typography variant="h5" component="h1" color="error">
                    {errorMessage}
                </Typography>
            </Container>
        )
    }
    return <ShipmentPreviewGuest />
}
